/**
 * The Merchant directory (ADR-0063, M12-1).
 *
 * A suspension is an operator's commercial decision about a Merchant's standing, and its reason is
 * a **closed enum** for the reason revocation's is (ADR-0024): free text in an append-only store
 * cannot be corrected and cannot be counted, and "how many Merchants were suspended for unpaid
 * fees" is a question somebody will ask. The note beside it is the operator's own words.
 *
 * **There is no compliance reason, on purpose.** A designated-party finding does not suspend a
 * Merchant; it freezes custody through Machine 2, and the screening half of `merchantRemittable`
 * refuses payment. A suspension stops intake and keeps paying (maintainer's ruling, 2026-09-14), so
 * a compliance reason here would name the wrong control.
 */
import { z } from 'zod';
import { renderedText } from './rendered-text.js';
export const MERCHANT_SUSPENSION_REASONS = [
    'contract_breach',
    'unpaid_fees',
    'fraud_suspected',
    'merchant_request',
    'other',
];
export const MERCHANT_REACTIVATION_REASONS = ['issue_resolved', 'merchant_request', 'suspended_in_error'];
const note = z.string().trim().min(1).max(500);
export const suspendMerchantSchema = z.object({
    reason: z.enum(MERCHANT_SUSPENSION_REASONS),
    note,
});
export const suspendMerchantRendered = renderedText(suspendMerchantSchema, {
    exempt: { note: 'stored only in the merchant.suspended event payload; no view renders it' },
});
export const reactivateMerchantSchema = z.object({
    reason: z.enum(MERCHANT_REACTIVATION_REASONS),
    note,
});
export const reactivateMerchantRendered = renderedText(reactivateMerchantSchema, {
    exempt: { note: 'stored only in the merchant.reactivated event payload; no view renders it' },
});
/**
 * Switch a Merchant between the live and the sandbox ledger tree (P2-7f, ADR-0083 §10).
 *
 * `sandbox` is the side the Merchant should be on, not a toggle: a request that would leave it where
 * it is is refused rather than flipping it back, so two operators acting at once cannot undo each
 * other. The note is the operator's own words, kept in the `merchant.amended` event.
 */
export const setMerchantSandboxSchema = z.object({
    sandbox: z.boolean(),
    note,
});
export const setMerchantSandboxRendered = renderedText(setMerchantSandboxSchema, {
    exempt: { note: 'stored only in the merchant.amended event payload; no view renders it' },
});
/**
 * Why a switch is refused, each with copy in three languages.
 *
 * The switch is a **setup act**: a Merchant's tree is read at posting time (ADR-0023), so moving one
 * that has Orders would split its books across both trees, and a key minted on one side would act
 * on the other while its holder believed otherwise. Going live is a second Merchant.
 */
export const MERCHANT_SANDBOX_ISSUE_CODES = ['sandbox_unchanged', 'merchant_has_orders', 'merchant_has_keys'];
/**
 * Whether one Merchant's Buyers are told (P4-3c, ADR-0078 §1).
 *
 * `notification_rules` is platform configuration, identical for every tenant; this is the one thing
 * above it that is not. An **override**, so a Merchant with no row inherits the platform rule — and
 * a rule switched off platform-wide is never quietly re-enabled by a stale row saying `true`.
 *
 * **Recorded by Ops on the Merchant's behalf**, because no route creates a Merchant human binding
 * (M13), so no Merchant can open a screen to set it. Named that way rather than "the Merchant's
 * switch", which would be a claim about who operates it.
 *
 * `enabled` is the side it should be on rather than a toggle, as the sandbox switch is, so two
 * operators acting at once cannot undo each other.
 */
export const setBuyerNoticeSchema = z.object({
    typeKey: z.string().min(1),
    enabled: z.boolean(),
    note,
});
export const setBuyerNoticeRendered = renderedText(setBuyerNoticeSchema, {
    exempt: {
        note: 'stored only in the merchant.amended event payload; no view renders it',
        typeKey: 'a catalogue key checked against NOTIFICATION_TYPES, never free text',
    },
});
/**
 * Why a Buyer-notice override is refused.
 *
 * `notice_not_a_buyer_notice` is the rule the fan-out states a second time by consulting an override
 * only for a buyer-audience type: an Ops alert about a Merchant is the platform's and not theirs, so
 * the dangerous direction stays closed even against a row written at a psql prompt.
 */
export const BUYER_NOTICE_ISSUE_CODES = ['notice_unknown', 'notice_not_a_buyer_notice', 'notice_unchanged'];
//# sourceMappingURL=directory.js.map